<script setup lang="ts">
    import {
        AlertDialog,
        AlertDialogAction,
        AlertDialogCancel,
        AlertDialogContent,
        AlertDialogDescription,
        AlertDialogFooter,
        AlertDialogHeader,
        AlertDialogTitle,
        AlertDialogTrigger,
    } from '@/components/ui/alert-dialog';
    import { useForm } from '@inertiajs/vue3';
    import { useQueryClient } from '@tanstack/vue-query';
    import { toast } from 'vue-sonner';
    import { useI18n } from 'vue-i18n';
    import { User } from '@/types';
    import { computed } from 'vue';
    import ActivateButton from '@/components/ActivateButton.vue';
    import DeactivateButton from '@/components/DeactivateButton.vue';
    import QueryKey from '@/enums/QueryKey';

    const queryClient = useQueryClient();

    const emit = defineEmits(['deleted', 'error']);

    const { t } = useI18n();

    interface Props {
        user: User;
    }

    const props = defineProps<Props>();

    const form = useForm({});

    const isActive = computed(() => {
        return props.user.deleted_at ? false : true;
    });

    const type = computed(() => {
        return isActive.value ? 'deactivate' : 'activate';
    });

    const path = computed(() => {
        return isActive.value ? 'v2.users.deactivate' : 'v2.users.activate';
    });

    const action = () => {
        form.post(route(path.value, props.user.id), {
            preserveScroll: true,
            onSuccess: () => {
                updateTable();
                toast.success(t(`users.${type.value}.success`));
                emit('deleted');
            },
            onError: (error) => {
                toast.error(t('forms.general.error'));
                emit('error', error);
            },
        });
    };

    const updateTable = () => {
        queryClient.invalidateQueries({ queryKey: [QueryKey.USERS] });
    };
</script>

<template>
    <AlertDialog>
        <AlertDialogTrigger as-child>
            <ActivateButton
                v-if="!isActive"
                class="mr-auto"
            />
            <DeactivateButton
                v-else
                class="mr-auto"
            />
        </AlertDialogTrigger>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>{{ t(`users.${type}.title`) }}</AlertDialogTitle>
                <AlertDialogDescription>{{ t(`users.${type}.content`) }}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel>{{ t(`users.${type}.no`) }}</AlertDialogCancel>
                <AlertDialogAction
                    @click="action"
                    :variant="isActive ? 'destructive' : 'default'"
                >
                    {{ t(`users.${type}.yes`) }}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
</template>
